import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { EmailSendData } from '../hooks/useSendEmail';

type EmailResponseDataInput = {
  open:boolean;
  onClose: () => void;
  data: EmailSendData | null;
  addVoucherCol: boolean;
}

const EmailResponseSummary = ({ open, onClose, data, addVoucherCol }:EmailResponseDataInput) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const errorColWidth = isMobile ? 160 : 320;

  const headerCellStyle = {
    fontWeight: 700,
    background: '#faf8fc',
    color: '#555',
    fontSize: 16,
    borderBottom: '2px solid #eee',
    zIndex: 1,
  };
  if(!data) {
    return null
  }
  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minWidth: 900,
          maxWidth: 1300,
          minHeight: 700,
          maxHeight: 700,
          borderRadius: 3,
          p: 0,
          boxShadow: 10,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          fontSize: 24,
          letterSpacing: 0.5,
          color: theme.palette.primary.main,
          background: '#faf8fc',
          pb: 2,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>Email Delivery Summary</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#888',
            '&:hover': { color: theme.palette.error.main, background: '#f4d8e2' },
          }}
        >
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          px={4}
          py={2}
          sx={{
            background: '#f4f5f7',
            borderBottom: '1px solid #eee',
          }}
        >
          <Chip
            icon={<CheckCircleRoundedIcon sx={{ color: '#178a4c' }} />}
            label={`Success: ${data.summary.success || 0}`}
            sx={{
              px: 2.5,
              py: 0.6,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 16,
              boxShadow: 1,
              letterSpacing: 0.2,
              bgcolor: '#eafaf1',
              color: '#178a4c',
            }}
          />
          <Chip
            icon={<ErrorRoundedIcon sx={{ color: '#d32f2f' }} />}
            label={`Failed: ${data.summary.failed || 0}`}
            sx={{
              px: 2.5,
              py: 0.6,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 16,
              boxShadow: 1,
              letterSpacing: 0.2,
              bgcolor: '#faeaea',
              color: '#d32f2f',
            }}
          />
          <Chip
            label={`Total: ${data.summary.total || 0}`}
            sx={{
              px: 2.5,
              py: 0.6,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 16,
              boxShadow: 1,
              letterSpacing: 0.2,
              bgcolor: '#f3e8fd',
              color: '#7b1fa2',
            }}
          />
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            borderRadius: 0,
            boxShadow: 'none',
            bgcolor: '#fff',
            maxHeight: 'calc(100% - 60px)', // subtract header+chips
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={headerCellStyle}>
                  #
                </TableCell>
                {addVoucherCol && <TableCell sx={headerCellStyle}>Voucher No#</TableCell>}
                <TableCell sx={headerCellStyle}>Recipient Name</TableCell>
                <TableCell sx={headerCellStyle}>Email</TableCell>
                <TableCell align="center" sx={headerCellStyle}>
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellStyle,
                    minWidth: errorColWidth,
                    maxWidth: errorColWidth,
                  }}
                >
                  Error
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.details.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={addVoucherCol ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.details.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      background: row.status === 'Failed' ? '#ffebee' : 'inherit',
                    }}
                  >
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {idx + 1}
                    </TableCell>
                    {addVoucherCol && <TableCell sx={{ fontWeight: 500 }}>{row?.vouchernumber || '-'}</TableCell>}
                    <TableCell sx={{ fontWeight: 500 }}>{row?.party || row?.recipient}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#555' }}>
                        {row.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={
                          row.status === 'Success' ? <CheckCircleRoundedIcon fontSize="small" /> : <ErrorRoundedIcon fontSize="small" />
                        }
                        label={row.status}
                        color={row.status === 'Success' ? 'success' : 'error'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: 0.2,
                          fontSize: 14,
                          px: 1.5,
                          py: 0.8,
                          bgcolor: row.status === 'Success' ? '#eafaf1' : '#faeaea',
                          color: row.status === 'Success' ? '#178a4c' : '#d32f2f',
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        color: row.status === 'Failed' ? '#d32f2f' : '#178a4c',
                        fontWeight: 500,
                        minWidth: errorColWidth,
                        maxWidth: errorColWidth,
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                      }}
                    >
                      {row.status === 'Failed' ? row.message || '-' : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default EmailResponseSummary;
